import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../context/AuthContext'
import SuperAdminLayout from '../components/SuperAdminLayout'
import { sendInviteEmail } from '../../../lib/emailService'

export default function SuperAdminInvite() {
  const { user } = useAuth()
  const [clubs, setClubs] = useState([])
  const [invites, setInvites] = useState([])
  const [formData, setFormData] = useState({ email: '', club_id: '' })
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  const fetchData = async () => {
    const { data: clubsData } = await supabase.from('clubs').select('*').order('name')
    const { data: invitesData } = await supabase.from('invites').select('*, clubs(name)').eq('role', 'club_admin').order('created_at', { ascending: false })
    if (clubsData) setClubs(clubsData)
    if (invitesData) setInvites(invitesData)
  }

  useEffect(() => { fetchData() }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(false)
    if (!formData.club_id) { setError('Please select a club'); setSaving(false); return }

    try {
      // Save invite record
      const { error: inviteError } = await supabase.from('invites').insert({
        email: formData.email,
        role: 'club_admin',
        club_id: formData.club_id,
        invited_by: user.id,
      })
      if (inviteError) { setError(inviteError.message); setSaving(false); return }

      // Get club name for email
      const club = clubs.find(c => c.id === formData.club_id)

      // Send invite email via Edge Function
      const result = await sendInviteEmail({
        email: formData.email,
        role: 'club_admin',
        clubName: club?.name || 'DSMS',
        chapterName: '',
      })

      if (!result.success) { setError(`Invite saved but email failed: ${result.error || 'Unknown error'}`); setSaving(false); return }

      setSuccess(true)
      setFormData({ email: '', club_id: '' })
      fetchData()
    } catch (err) {
      setError(`Failed to send invite: ${err.message}`)
    }
    setSaving(false)
  }

  const inputClass = "bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 focus:border-green-600 text-gray-900 dark:text-white px-4 py-3 text-sm outline-none transition-colors w-full"
  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  return (
    <SuperAdminLayout>
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl lg:text-4xl font-black uppercase tracking-tight mb-2 text-gray-900 dark:text-white">Invite Club Admin</h1>
        <p className="text-gray-500 text-sm">Send an email invite to onboard a new club administrator</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 sm:p-8">
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">New Invite</div>
          {success && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm mb-4">Invite sent! The admin will receive an email with login instructions.</div>}
          {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Email Address</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="admin@club.com" required className={inputClass} />
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Assign to Club</label>
              <select value={formData.club_id} onChange={(e) => setFormData({ ...formData, club_id: e.target.value })} required className={inputClass}>
                <option value="">Select a club...</option>
                {clubs.map((club) => <option key={club.id} value={club.id}>{club.name}</option>)}
              </select>
            </div>
            <button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors mt-2">
              {saving ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
        </div>

        <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6 sm:p-8">
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Pending Invites</div>
          {invites.length === 0 ? (
            <div className="text-gray-400 text-sm">No invites sent yet</div>
          ) : (
            <div className="flex flex-col gap-3">
              {invites.map((invite) => (
                <div key={invite.id} className="border border-gray-200 dark:border-white/10 p-4">
                  <div className="font-semibold text-sm mb-1 text-gray-900 dark:text-white">{invite.email}</div>
                  <div className="text-gray-500 text-xs mb-2">{invite.clubs?.name}</div>
                  <div className="flex justify-between items-center">
                    <span className={`text-xs font-bold uppercase tracking-widest px-2 py-1 ${invite.accepted ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'}`}>
                      {invite.accepted ? 'Accepted' : 'Pending'}
                    </span>
                    <span className="text-gray-400 text-xs">{new Date(invite.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  )
}